import React, {
    ComponentType,
    ElementType,
    PropsWithChildren,
    ReactNode,
    SyntheticEvent,
    useCallback,
} from 'react';
import { Tabs as CommonTabs, TabsProps as CommonTabsProps, tabsClasses } from '@mui/material';

import { Tab, TabProps } from '../Tab';


export type TabOption<TValue = any> = {
    icon?: TabProps['icon'];
    value: TValue;
    label: ReactNode;
};

export type TabsProps<TOption extends TabOption = TabOption, TTab extends ElementType = 'div'> =
Omit<CommonTabsProps, 'TabComponent' | 'size' | 'onChange' | 'value' | 'variant'> & {
    variant: TabProps['variant'];
    gray?: boolean;
    value: any;
    TabComponent?: ComponentType<TabProps<TTab>>;
    onChange: (value: any, e?: SyntheticEvent) => void;
    options: TOption[];
    size?: 'large' | 'small';
    fullWidth?: boolean;
};

export function Tabs<TOption extends TabOption = TabOption, TTab extends ElementType = 'div'>({
    variant,
    gray,
    value,
    onChange,
    options,
    TabComponent = Tab,
    size = 'large',
    fullWidth,
    sx,
}: PropsWithChildren<TabsProps<TOption, TTab>>) {
    const handleChange = useCallback<CommonTabsProps['onChange']>(
        (e, value) => onChange(value, e),
        [onChange],
    );
    return (
        <CommonTabs
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{
                sx: ({ spacing }) => ({
                    display: variant === 'button' && 'none',
                    height: spacing(1),
                    backgroundColor: 'general.primary50',
                    borderRadius: `${spacing(1)} ${spacing(1)} 0 0`,
                }),
            }}
            sx={[{
                width: fullWidth ? '100%' : 'max-content',
                minHeight: variant === 'text' ? '36px' : size === 'large' ? '56px' : '40px',
                p: variant === 'button' && 1,
                borderBottomWidth: variant === 'text' ? '1px' : '0',
                borderBottomStyle: 'solid',
                borderBottomColor: 'grayscale.shade40',
                borderBottomLeftRadius: '2px',
                borderBottomRightRadius: '2px',
                borderRadius: variant === 'button' && '100px',
                backgroundColor:
                    variant === 'button' && (gray ? 'grayscale.shade10' : 'grayscale.shade0'),

                [`& .${tabsClasses.flexContainer}`]: {
                    minHeight: '100%',
                    ...variant === 'button' && {
                        '& > *': {
                            flex: 1,
                        },
                    },
                    '& > * + *': {
                        ml: variant === 'text' ? 8 : 2,
                    },
                },
            }, ...Array.isArray(sx) ? sx : [sx]]}
        >
            {options.map(props => (
                <TabComponent
                    variant={variant}
                    key={props.value}
                    gray={gray}
                    active={props.value === value}
                    {...props}
                />
            ))}
        </CommonTabs>
    );
}
